import { ApiClientsError } from '#pplx/frontend/api-clients/core/errors.js';
import { MAX_RETRIES } from '#pplx/frontend/api-clients/core/utils.js';
import { getLinkedAccounts } from '#pplx/frontend/auth/queries/getLinkedAccounts.js';
import { getAccountHeaders } from '#pplx/frontend/browser-storage/activeAccount.js';
import { SafeLocalStorage } from '#pplx/frontend/browser-storage/localStorage.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { getActiveRegistration, getActivePushSubscription, isBrowserPushSupported, } from '#pplx/frontend/push-notifications/internal/capability.js';
import { notificationsClient } from '#pplx_generated/typespec/rest/notifications/client.js';
const WEB_PUSH_REGISTERED_ACCOUNTS_KEY = 'pplx.web-push.registered-accounts';
const WEB_PUSH_PUBLIC_KEY_TTL_MS = 60_000;
let cachedWebPushPublicKey;
let inFlightWebPushPublicKeyPromise;
/**
 * Checks whether the current browser can use web Push through this app's service worker.
 *
 * Mobile browsers are excluded because this flow is currently desktop web only.
 *
 * @returns `true` when Service Workers and PushManager are available on a non-mobile browser.
 */
export function isPushSupported() {
    return isBrowserPushSupported();
}
/**
 * Reads the current browser Push notification permission.
 *
 * @returns The browser notification permission state.
 */
export function getPushPermission() {
    if (typeof Notification === 'undefined') {
        return 'default';
    }
    return Notification.permission;
}
/**
 * Checks whether Push is currently enabled for this browser.
 *
 * @returns `true` when this browser has an active Push subscription; otherwise `false`.
 */
export async function isPushEnabled() {
    return (await getActivePushSubscription()) !== null;
}
/**
 * The accounts this browser's subscription is already registered for, keyed by endpoint so a
 * rotated subscription (new endpoint) invalidates the record. Per-browser, not per-account, so it
 * stays un-namespaced like the restore marker. Lets the restore-on-every-reload path re-POST only
 * the accounts that aren't registered yet instead of the whole set each time.
 */
function getRegisteredAccounts(endpoint) {
    const raw = SafeLocalStorage.getItem(WEB_PUSH_REGISTERED_ACCOUNTS_KEY, { global: true });
    if (!raw) {
        return new Set();
    }
    try {
        const parsed = JSON.parse(raw);
        if (typeof parsed !== 'object' ||
            parsed === null ||
            !('endpoint' in parsed) ||
            parsed.endpoint !== endpoint ||
            !('accountIds' in parsed) ||
            !Array.isArray(parsed.accountIds)) {
            return new Set();
        }
        return new Set(parsed.accountIds.filter((id) => typeof id === 'string'));
    }
    catch {
        return new Set();
    }
}
function writeRegisteredAccounts(endpoint, accountIds) {
    SafeLocalStorage.setItem(WEB_PUSH_REGISTERED_ACCOUNTS_KEY, JSON.stringify({ endpoint, accountIds: [...accountIds] }), { global: true });
}
function forgetRegisteredAccounts() {
    SafeLocalStorage.removeItem(WEB_PUSH_REGISTERED_ACCOUNTS_KEY, { global: true });
}
async function getWebPushPublicKey(reason) {
    if (cachedWebPushPublicKey && cachedWebPushPublicKey.expiresAtMs > Date.now()) {
        return cachedWebPushPublicKey.value;
    }
    if (inFlightWebPushPublicKeyPromise) {
        return inFlightWebPushPublicKeyPromise;
    }
    inFlightWebPushPublicKeyPromise = fetchWebPushPublicKey(reason)
        .then((vapidPublicKey) => {
        cachedWebPushPublicKey = {
            value: vapidPublicKey,
            expiresAtMs: Date.now() + WEB_PUSH_PUBLIC_KEY_TTL_MS,
        };
        return vapidPublicKey;
    })
        .finally(() => {
        inFlightWebPushPublicKeyPromise = undefined;
    });
    return inFlightWebPushPublicKeyPromise;
}
async function fetchWebPushPublicKey(reason) {
    const { data, error: keyError, response: keyResponse, } = await notificationsClient.GET('/rest/notifications/web-push/public-key', reason, {
        numRetries: MAX_RETRIES,
    });
    const vapidPublicKey = data?.public_key;
    if (!vapidPublicKey || keyError) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: `Failed to get web push public key`,
            cause: keyError,
            status: keyResponse.status ?? 0,
        });
    }
    return vapidPublicKey;
}
/**
 * The NextAuth userIds of every account signed into this browser, or `[]` when the list can't be
 * resolved (so the caller falls back to a single un-scoped registration).
 */
async function getLinkedAccountIds(reason) {
    try {
        const accounts = await getLinkedAccounts({ reason });
        return accounts.map((account) => account.userId);
    }
    catch (error) {
        logger.error(error);
        return [];
    }
}
/**
 * Resolves this browser's Push subscription — the existing one, or a freshly minted one when there
 * is none — or `null` on failure (logged). `createdNew` flags a freshly minted subscription so the
 * caller can clean up the orphan if no backend registration ends up succeeding.
 */
async function getOrCreateSubscription(registration, reason) {
    try {
        const existing = await getActivePushSubscription();
        if (existing) {
            return { subscription: existing, createdNew: false };
        }
        const vapidPublicKey = await getWebPushPublicKey(reason);
        const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true, // required by Chrome
            applicationServerKey: vapidPublicKey,
        });
        return { subscription, createdNew: true };
    }
    catch (error) {
        logger.error(error);
        return null;
    }
}
/**
 * Registers the one browser subscription with the backend for each signed-in account: one
 * `X-Pplx-Account` request per linked account, or a single un-scoped request (the primary/active
 * account) when the linked-accounts list can't be resolved. Skips accounts already registered for
 * this endpoint, so the restore-on-every-reload path doesn't re-POST the whole set. Returns whether
 * at least one registration succeeded (or was already in place).
 */
async function registerForAllAccounts({ subscription, reason, }) {
    const accountIds = await getLinkedAccountIds(reason);
    // Linked-accounts unresolved: one un-scoped registration for the active / primary account. It's a
    // single call, not a fan-out, so nothing to dedup.
    if (accountIds.length === 0) {
        return registerSubscriptionForAccount({ subscription, accountId: undefined, reason });
    }
    // Multi-account: restore re-runs this on every reload, so register only the accounts that aren't
    // already registered for this endpoint. A steady-state reload becomes a no-op; a just-added
    // account is the only POST.
    const registered = getRegisteredAccounts(subscription.endpoint);
    const pending = accountIds.filter((accountId) => !registered.has(accountId));
    if (pending.length === 0) {
        return true;
    }
    const results = await Promise.all(pending.map((accountId) => registerSubscriptionForAccount({ subscription, accountId, reason }).then((ok) => {
        if (ok) {
            registered.add(accountId);
        }
        return ok;
    })));
    writeRegisteredAccounts(subscription.endpoint, registered);
    return results.some(Boolean);
}
/**
 * The active service worker registration that supports Push, or `null` when there is none, Push
 * isn't available on it, or the lookup fails (logged).
 */
async function getPushRegistration() {
    try {
        const registration = await getActiveRegistration();
        if (!registration?.pushManager) {
            return null;
        }
        return registration;
    }
    catch (error) {
        logger.error(error);
        return null;
    }
}
/**
 * Enables web Push for this browser.
 *
 * A browser has one Push subscription, but several accounts can be signed in, so the single
 * subscription is registered for *every* signed-in account (one `X-Pplx-Account` request each) —
 * each then receives its own pushes through the shared endpoint.
 *
 * @param options - Enable options.
 * @param options.reason - Observability reason string passed through enable requests.
 * @returns `true` when Push was enabled with the backend; otherwise `false`.
 */
export async function enablePush({ reason }) {
    const registration = await getPushRegistration();
    if (!registration) {
        return false;
    }
    const resolved = await getOrCreateSubscription(registration, reason);
    if (!resolved) {
        return false;
    }
    const { subscription, createdNew } = resolved;
    const registered = await registerForAllAccounts({ subscription, reason });
    // We minted a subscription but the backend recorded none of it — don't leave it orphaned.
    if (!registered && createdNew) {
        await subscription.unsubscribe().catch((error) => logger.error(error));
    }
    return registered;
}
/**
 * Disables web Push.
 *
 * @param options - Disable options.
 * @param options.accountId - Optional account id to remove from the shared browser endpoint.
 * @param options.reason - Observability reason string passed through disable requests.
 * @returns `true` when the requested Push disable finished; otherwise `false`.
 */
export async function disablePush({ accountId, reason, }) {
    if (accountId !== undefined) {
        return disablePushForAccount({ accountId, reason });
    }
    let subscription;
    try {
        subscription = await getActivePushSubscription();
        if (!subscription)
            return false;
    }
    catch (error) {
        logger.error(error);
        return false;
    }
    try {
        const success = await subscription.unsubscribe();
        if (!success)
            return false;
    }
    catch (error) {
        logger.error(error);
        return false;
    }
    // The subscription (and its endpoint) is gone, so its registered-accounts record is stale.
    forgetRegisteredAccounts();
    try {
        const { error: unsubError, response: unsubResponse } = await notificationsClient.DELETE('/rest/notifications/web-push/unsubscribe', reason, {
            body: {
                endpoint: subscription.endpoint,
            },
            numRetries: MAX_RETRIES,
        });
        if (unsubError) {
            throw new ApiClientsError('API_CLIENTS_ERROR', {
                message: `Failed to unsubscribe from web push`,
                cause: unsubError,
                status: unsubResponse.status ?? 0,
            });
        }
    }
    catch (error) {
        logger.error(error);
        // Do not return early--our backend falling over should not stop the browser from unsubscribing
    }
    return true;
}
/**
 * POSTs this browser's single Push subscription to the backend for one account. With an explicit
 * `accountId` the request is attributed to that account via `X-Pplx-Account` (so the backend
 * records the device row under it); without one it falls back to the tab's active / primary
 * account, exactly as the single-account flow always has. Returns whether the backend accepted it;
 * failures are logged.
 */
async function registerSubscriptionForAccount({ subscription, accountId, reason, }) {
    try {
        const { error: subError, response: subResponse } = await notificationsClient.POST('/rest/notifications/web-push/subscribe', reason, {
            body: {
                endpoint: subscription.endpoint,
                keys: subscription.toJSON().keys ?? {},
            },
            numRetries: MAX_RETRIES,
            headers: getAccountHeaders(accountId),
        });
        if (subError) {
            throw new ApiClientsError('API_CLIENTS_ERROR', {
                message: `Failed to subscribe to web push`,
                cause: subError,
                status: subResponse.status ?? 0,
            });
        }
    }
    catch (error) {
        logger.error(error);
        return false;
    }
    return true;
}
// Remove one account's backend device row without deleting the shared browser Push subscription.
async function disablePushForAccount({ accountId, reason, }) {
    let subscription;
    try {
        subscription = await getActivePushSubscription();
        if (!subscription)
            return false;
    }
    catch (error) {
        logger.error(error);
        return false;
    }
    try {
        const { error: unsubError, response: unsubResponse } = await notificationsClient.DELETE('/rest/notifications/web-push/unsubscribe', reason, {
            body: {
                endpoint: subscription.endpoint,
            },
            numRetries: MAX_RETRIES,
            headers: getAccountHeaders(accountId),
        });
        if (unsubError) {
            throw new ApiClientsError('API_CLIENTS_ERROR', {
                message: `Failed to unregister account from web push`,
                cause: unsubError,
                status: unsubResponse.status ?? 0,
            });
        }
    }
    catch (error) {
        logger.error(error);
        return false;
    }
    // Drop the signed-out account so it re-registers if it's ever added back to this browser.
    const registered = getRegisteredAccounts(subscription.endpoint);
    if (registered.delete(accountId)) {
        writeRegisteredAccounts(subscription.endpoint, registered);
    }
    return true;
}
/**
 * Sends a test push notification to this browser's active Push API subscription.
 *
 * @param options - Test notification options.
 * @param options.reason - Observability reason string passed through the test notification request.
 * @returns `true` when the backend accepted the test notification request; otherwise `false`.
 */
export async function sendTestPush({ reason }) {
    let subscription;
    try {
        subscription = await getActivePushSubscription();
        if (!subscription)
            return false;
    }
    catch (error) {
        logger.error(error);
        return false;
    }
    try {
        const { error: testError, response: testResponse } = await notificationsClient.POST('/rest/notifications/web-push/test', reason, {
            body: {
                endpoint: subscription.endpoint,
            },
            numRetries: MAX_RETRIES,
        });
        if (testError) {
            throw new ApiClientsError('API_CLIENTS_ERROR', {
                message: `Failed to send test web push`,
                cause: testError,
                status: testResponse.status ?? 0,
            });
        }
    }
    catch (error) {
        logger.error(error);
        return false;
    }
    return true;
}
//# sourceMappingURL=pushNotifications.js.map